import { gql, useMutation } from '@apollo/client'
import { InitialEditorStateType } from '@lexical/react/LexicalComposer'
import { EditorState, SerializedEditorState } from 'lexical'
import { useEffect, useMemo, useState } from 'react'
import { usePrevious } from 'react-use'

import useBrands from 'pared/layouts/hooks/useBrands'

import { IQueryDataType } from './useListGlCodesAndGroups'

export type IOptionType = {
  manageType: 'addGlGroup'
} & (
  | {
      id: string
      pAndLName: string
    }
  | {
      isFormulaGroup?: boolean
    }
)

export interface IDataType {
  glGroupName?: string
  value: { name: string; formula: InitialEditorStateType }
  onNameChange: (value: NonNullable<IDataType['value']>['name']) => void
  onFormulaChange: (value: NonNullable<IDataType['value']>['formula']) => void
  loading: boolean
  errorMessage: { name?: string; formula?: string }
  submit: () => Promise<void>
}

const createMutate = gql`
  mutation CreateGlGroup(
    $name: String!
    $parentGroupId: BigInt
    $formula: JSON
  ) {
    createGlGroup(
      input: {
        glGroup: {
          name: $name
          displayName: $name
          parentGroupId: $parentGroupId
          formula: $formula
        }
      }
    ) {
      glGroup {
        id
      }
    }
  }
`

export const isEditorState = (
  editorState: InitialEditorStateType,
): editorState is EditorState =>
  Boolean(editorState) &&
  !(typeof editorState === 'string') &&
  !(typeof editorState === 'function')

export const eitorStateValidate = (
  editorState: InitialEditorStateType,
  isSubmitting: boolean,
) => {
  if (!isSubmitting) return

  if (!isEditorState(editorState)) return `Couldn't be empty`

  const data = editorState.toJSON() as SerializedEditorState<{
    type: string
    version: number
    children: Record<string, unknown>[]
  }>
  const allData = data.root.children.map((d) => d.children).flat()

  if (allData.length === 0) return `Couldn't be empty`

  const invalidateText = allData
    .filter((a, index) => {
      switch (a.type) {
        case 'text':
          return !['+', '-', ''].includes((a.text as string).trim())
        case 'select-node':
          return (
            allData[index - 1]?.type === 'select-node' ||
            (allData[index - 1]?.type === 'text' &&
              (allData[index - 1]?.text as string).trim() === '' &&
              allData[index - 2]?.type === 'select-node')
          )
        default:
          return true
      }
    })
    .map((a) => {
      switch (a.type) {
        case 'text': {
          if (['+@', '-@'].includes((a.text as string).trim()))
            return `Add the space between the operator and @: ${a.text}`

          return `Invalid: ${a.text}`
        }
        case 'select-node':
          return `Add the operator before: ${a.text}`
        default:
          return `Invalid: ${a.text}`
      }
    })
    .reduce(
      (result, text) => (result.includes(text) ? result : [...result, text]),
      [] as string[],
    )

  return invalidateText.length > 0 ? invalidateText.join(', ') : undefined
}

const useAddGlGroup = (
  data: IQueryDataType | null,
  option: IOptionType | null,
  close: () => void,
) => {
  const addGlGroupHook = useState<IDataType['value']>({
    name: '',
    formula: null,
  })
  const isSubmittingHook = useState(false)
  const prevManageType = usePrevious(option?.manageType)
  const { brand } = useBrands()
  const [createGlGroup, { loading }] = useMutation(createMutate, {
    refetchQueries: ['ListGlCodesAndGroups'],
    awaitRefetchQueries: true,
  })
  const options = useMemo(
    () =>
      !option
        ? []
        : (data?.listGlGroups.nodes || []).reduce(
            (result, n) =>
              result.some((r) => r === n.name) ? result : [...result, n.name],
            [] as string[],
          ),
    [data, option],
  )

  useEffect(() => {
    if (
      prevManageType !== option?.manageType &&
      option?.manageType !== 'addGlGroup'
    ) {
      addGlGroupHook[1]({
        name: '',
        formula: null,
      })
      isSubmittingHook[1](false)
    }
  }, [option, prevManageType, addGlGroupHook, isSubmittingHook])

  return useMemo((): IDataType | undefined => {
    if (option?.manageType !== 'addGlGroup') return

    const [value, onChange] = addGlGroupHook
    const [isSubmitting, setIsSubmitting] = isSubmittingHook
    const validator = (isSubmitting: boolean) => {
      const name = (() => {
        if (!value.name && isSubmitting) return `Couldn't be empty`

        if (options.includes(value.name)) return `${value.name} exists`
      })()
      const formula =
        'isFormulaGroup' in option && option.isFormulaGroup
          ? eitorStateValidate(value.formula, isSubmitting)
          : undefined

      return { name, formula }
    }

    return {
      glGroupName: 'pAndLName' in option ? option.pAndLName : undefined,
      value,
      onNameChange: (name: string) => onChange({ ...value, name }),
      onFormulaChange: (formula: InitialEditorStateType) =>
        onChange({ ...value, formula }),
      loading,
      errorMessage: validator(isSubmitting),
      submit: async () => {
        const errorMessage = validator(true)
        const hasError = Boolean(errorMessage.name || errorMessage.formula)

        if (!isSubmitting) setIsSubmitting(true)

        if (loading || hasError) return

        await createGlGroup({
          variables: {
            parentGroupId: 'id' in option ? option.id.split('-')[1] : null,
            name: value.name,
            formula: isEditorState(value.formula)
              ? value.formula.toJSON()
              : null,
          },
        })
        setIsSubmitting(false)
        close()
      },
    }
  }, [option, close, addGlGroupHook, isSubmittingHook, loading, brand, options])
}

export default useAddGlGroup
